import React from "react";
import { differenceInCalendarDays, parseISO } from "date-fns";
import { DateRangePresetFilter, List } from "components";
import { JOB_TYPES } from "@/lib/constants";
import { ROUTE, calculateTotalHours } from "@/lib/utils";
import { Tooltip, TooltipContent, TooltipTrigger } from "ui/components/tooltip";
import { Icon } from "components";
import { DateCell } from "@/components/date/cell";
import { EmployeeCell } from "components/cell/employee/cell";
import { HoursCell } from "@/components/hours/cell";
import ExtendedJobNameCell from "@/components/cells/jobs/name-cell/extended-name";
import { useQuery } from "@tanstack/react-query";
import { getItemsFromCollection } from "directus/src/operations";
import { getEmployeeFields } from "components/user/config";

const JobTimesheetCell = ({ jobId }) => {
  const { data } = useQuery({
    queryKey: ["job-timesheet", jobId],
    queryFn: () =>
      getItemsFromCollection("timesheet", {
        filter: { job: { _eq: jobId } },
        fields: ["logged_hours", "date"],
        limit: -1,
      }),
    enabled: !!jobId,
  });

  return (
    <HoursCell getValue={() => calculateTotalHours(data || [], "logged_hours")} />
  );
};

const JobTimesheetLast7DaysCell = ({ jobId }) => {
  const { data } = useQuery({
    queryKey: ["job-timesheet", jobId],
    queryFn: () =>
      getItemsFromCollection("timesheet", {
        filter: { job: { _eq: jobId } },
        fields: ["logged_hours", "date"],
        limit: -1,
      }),
    enabled: !!jobId,
  });

  const filtered = (data || []).filter((t) => {
    const date = t.date ? parseISO(t.date) : null;
    if (!date) return false;
    const diff = differenceInCalendarDays(new Date(), date);
    return diff > 0 && diff <= 7;
  });

  return (
    <HoursCell getValue={() => calculateTotalHours(filtered, "logged_hours")} />
  );
};

const InProgressJobsList = ({ coordinatorId }) => {

  const fields = [
    "id",
    "name",
    "status",
    "type",
    "access.employee",
    "access.role",
    "start_date",
    "end_date",
    getEmployeeFields("coordinator"),
    "project.name",
    "project.id",
    "project.project_id",
    getEmployeeFields("project.coordinator"),
    getEmployeeFields("project.account_manager"),
    getEmployeeFields("project.sales_coordinator"),
    "extensions.start_date",
    "extensions.end_date",
    "extensions.date_created",
    "extensions.reason.label",
    "allocated_hours.hours",
    "bucket_job_config.hours",
  ];

  const columns = [
    {
      id: "name",
      header: "Name",
      accessorKey: "name",
      sortable: true,
      cell: ExtendedJobNameCell,
    },
    {
      id: "project",
      header: "Project",
      accessorKey: "project.name",
    },
    {
      id: "type",
      header: "Type",
      accessorKey: "type",
      cell: ({ getValue }) => {
        const type = JOB_TYPES[getValue()];
        if (!type) return null;
        return (
          <Tooltip>
            <TooltipTrigger>
              <Icon icon={type.icon} className="text-muted-foreground size-4" />
            </TooltipTrigger>
            <TooltipContent>{type.label}</TooltipContent>
          </Tooltip>
        );
      },
      sortable: false,
    },
    {
      id: "start_date",
      header: "Start Date",
      accessorKey: "start_date",
      sortable: true,
      cell: DateCell,
    },
    {
      id: "end_date",
      header: "End / Extended Date",
      accessorKey: "end_date",
      sortable: true,
      cell: ({ row, getValue }) => (
        <div className="flex flex-col">
          {DateCell({ getValue })}
          {row.original.extensions?.length > 0 && row.original.extensions?.[0]?.start_date && (
            <span className="text-red-500 text-xs">
              {DateCell({ getValue: () => row.original.extensions?.[0]?.start_date })}
            </span>
          )}
        </div>
      ),
    },
    {
      id: "extensions",
      header: "Extension reason",
      accessorKey: "extensions",
      cell: ({ row }) => {
        const extensions = row.original.extensions || [];
        const lastExtension = extensions.length > 0 ? extensions[extensions.length - 1] : null;
        return lastExtension?.reason?.label;
      },
    },
    {
      id: "coordinator",
      header: "JC",
      accessorKey: "coordinator",
      cell: EmployeeCell,
    },
    {
      id: "project.coordinator",
      header: "PC",
      accessorKey: "project.coordinator",
      cell: EmployeeCell,
    },
    {
      id: "project.account_manager",
      header: "AM",
      accessorKey: "project.account_manager",
      cell: EmployeeCell,
    },
    {
      id: "project.sales_coordinator",
      header: "SC",
      accessorKey: "project.sales_coordinator",
      cell: EmployeeCell,
    },
    {
      id: "timesheet-logged",
      header: "Logged",
      accessorKey: "id",
      cell: ({ getValue }) => <JobTimesheetCell jobId={getValue()} />,
    },
    {
      id: "timesheet-logged-7d",
      header: "Logged last 7 days",
      accessorKey: "id",
      cell: ({ getValue }) => <JobTimesheetLast7DaysCell jobId={getValue()} />,
    },
  ];

  const filters = {
    access: {
      employee: {
        _eq: coordinatorId,
      },
      role: {
        _in: ["account_manager", "sales_coordinator", "project_coordinator", "job_coordinator"]
      },
    },
    status: {
      _eq: "in_progress",
    },
  };

  const deep = {
    extensions: {
      _sort: ["date_created"],
    },
  };

  return (
    <div key={coordinatorId}>
      <List
        title="In Progress Jobs"
        storageKey={`overview-in-progress-jobs-${coordinatorId}`}
        endPoint="jobs"
        filters={{ ...filters }}
        columns={columns}
        meta={{ fields, deep }}
        sortBy="project.name"
        sortOrder="asc"
        href={(item) => item.extensions?.length > 0 ? ROUTE.JOB_EXTENSIONS(item.id) : ROUTE.JOB(item.id)}
        showSearch={false}
      />
    </div>
  );
};

export default InProgressJobsList;


